// src/routes/domains/germany/germany.routes.tsx

import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { createProtectedRoute } from '../../core/createProtectedRoute';
import type { AppRoute } from '../../core/types';

// ------------------------------
// Public marketing hub (eager, if exists)
// ------------------------------
// import GermanyImmigrationHub from '@/pages/marketing/GermanyImmigrationHub';

// ------------------------------
// Lazy‑loaded Germany components (absolute imports)
// ------------------------------

// GermanyImmigrationFlightDeck, GermanyJourneyMap, and GermanySavedJourneyMaps
// were retired 2026-08-15: they read from a pre-UPRM localStorage journey
// system that the now-rebuilt assessment pathways no longer write to (only
// getLifecycleService's real UPRM journey state does), and
// GermanySavedJourneyMaps additionally fabricated fake week-by-week
// milestone timelines with no real backing. Archived to
// archive/germany-legacy-dashboard/ — all three routes below now redirect to
// the real, UPRM-wired /journey-compass.
const GermanyToolsEcosystem = lazy(() => import('@/pages/GermanyToolsEcosystem'));
const GermanyEvisaGateway = lazy(() => import('@/pages/GermanyEvisaGateway'));
const GermanyVisaFormConductor = lazy(() => import('@/pages/GermanyVisaFormConductor'));
const GermanyReunificationAccelerator = lazy(() => import('@/pages/GermanyReunificationAccelerator'));

// Assessments
const GermanyOpportunityCardAssessment = lazy(() => import('@/pages/GermanyOpportunityCardAssessment'));
const GermanyJobSeekerAssessment = lazy(() => import('@/pages/GermanyJobSeekerAssessment'));
const GermanyEUBlueCardAssessment = lazy(() => import('@/pages/GermanyEUBlueCardAssessment'));
const GermanySkilledImmigrationAssessment = lazy(() => import('@/pages/GermanySkilledImmigrationAssessment'));
const GermanyStudentAssessment = lazy(() => import('@/pages/GermanyStudentAssessment'));
const GermanyFamilyReunionAssessment = lazy(() => import('@/pages/GermanyFamilyReunionAssessment'));
const GermanyFreelancerAssessment = lazy(() => import('@/pages/GermanyFreelancerAssessment'));
const GermanyAssessmentResultAnalysis = lazy(() => import('@/pages/GermanyAssessmentResultAnalysis'));
const GermanySkilledCompanion = lazy(() => import('@/pages/journey/GermanySkilledCompanion'));
const GermanyCompanionResults = lazy(() => import('@/pages/journey/GermanyCompanionResults'));

// ------------------------------
// Germany Routes Definition
// ------------------------------
export const germanyRoutes: AppRoute[] = [
  // Optional public marketing hub (uncomment if needed)
  // {
  //   id: 'germany.marketing_hub',
  //   domain: 'germany',
  //   path: '/germany-immigration-hub',
  //   element: createProtectedRoute(<GermanyImmigrationHub />, {
  //     protected: false,
  //     meta: { title: 'Germany Immigration Hub', region: 'DE', intent: 'guide' },
  //   }),
  // },

  // Core tools & dashboards
  // Retired — see the archival note by the imports above.
  { id: 'germany.flight_deck', domain: 'germany', path: '/germany-immigration-flight-deck', element: <Navigate to="/journey-compass" replace /> },
  {
    id: 'germany.tools_ecosystem',
    domain: 'germany',
    path: '/de-tools-ecosystem',
    element: createProtectedRoute(<GermanyToolsEcosystem />, {
      tier: 'premium',
      meta: {
        title: 'Germany Tools Ecosystem',
        region: 'DE',
        intent: 'dashboard',
        aiTags: ['germany', 'ecosystem', 'tools'],
      },
    }),
  },
  {
    id: 'germany.evisa_gateway',
    domain: 'germany',
    path: '/germany-evisa-gateway',
    element: createProtectedRoute(<GermanyEvisaGateway />, {
      tier: 'premium',
      meta: {
        title: 'Germany eVisa Gateway',
        region: 'DE',
        intent: 'gateway',
        aiTags: ['evisa', 'gateway', 'germany'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // GermanyToolsEcosystem.tsx catalog id 'evisa-gateway'.
        toolId: 'de-evisa-gateway',
        category: 'integration',
      },
    }),
  },
  {
    id: 'germany.visa_form',
    domain: 'germany',
    path: '/germany-visa-form',
    element: createProtectedRoute(<GermanyVisaFormConductor />, {
      tier: 'premium',
      meta: {
        title: 'Germany Visa Form Conductor',
        region: 'DE',
        intent: 'form',
        aiTags: ['visa', 'form', 'application'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // GermanyToolsEcosystem.tsx catalog id 'visa-form'.
        toolId: 'de-visa-form',
        category: 'documents',
      },
    }),
  },
  // Retired — see the archival note by the imports above.
  { id: 'germany.journey_map', domain: 'germany', path: '/germany-journey-map', element: <Navigate to="/journey-compass" replace /> },
  // Retired — see the archival note by the imports above.
  { id: 'germany.saved_journey_maps', domain: 'germany', path: '/germany-saved-journey-maps', element: <Navigate to="/journey-compass" replace /> },
  {
    id: 'germany.reunification_accelerator',
    domain: 'germany',
    path: '/germany-reunification-accelerator',
    element: createProtectedRoute(<GermanyReunificationAccelerator />, {
      tier: 'premium',
      meta: {
        title: 'Germany Reunification Accelerator',
        region: 'DE',
        intent: 'tool',
        aiTags: ['reunification', 'family', 'accelerator'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // GermanyToolsEcosystem.tsx catalog id 'reunification-accelerator'.
        toolId: 'de-reunification-accelerator',
        category: 'planning',
      },
    }),
  },
  {
    // Retired 2026-08-29 — unified into /interview-simulator (all countries, one shared
    // trainer). The old dedicated component also fabricated a per-nationality "risk level"
    // with no real source; archived rather than kept alive. See PROJECT_HISTORY.md.
    id: 'germany.interview_simulator',
    domain: 'germany',
    path: '/germany-interview-simulator',
    element: <Navigate to="/interview-simulator" replace />,
  },

  // Companion (rule-based vault — same pattern as Canada PR Companion; not assessment-chat)
  {
    id: 'germany.skilled_companion',
    domain: 'germany',
    path: '/germany-skilled-companion',
    element: createProtectedRoute(<GermanySkilledCompanion />, {
      tier: 'premium',
      meta: {
        title: 'Germany Skilled Companion',
        region: 'DE',
        intent: 'companion',
        aiTags: ['germany', 'companion', 'blueprint'],
        toolId: 'de-skilled-companion',
        category: 'companion',
      },
    }),
  },
  {
    id: 'germany.companion_results',
    domain: 'germany',
    path: '/germany-companion-results',
    element: createProtectedRoute(<GermanyCompanionResults />, {
      tier: 'premium',
      meta: {
        title: 'Germany Companion Results',
        region: 'DE',
        intent: 'companion',
        aiTags: ['germany', 'companion', 'vault'],
        toolId: 'de-companion-results',
        category: 'companion',
      },
    }),
  },

  // Assessments
  {
    id: 'germany.opportunity_card',
    domain: 'germany',
    path: '/germany-opportunity-card-assessment',
    element: createProtectedRoute(<GermanyOpportunityCardAssessment />, {
      tier: 'premium',
      meta: {
        title: 'Germany Opportunity Card Assessment',
        region: 'DE',
        intent: 'assessment',
        aiTags: ['opportunity card', 'chancenkarte', 'assessment'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // GermanyToolsEcosystem.tsx catalog id 'opportunity-card'.
        toolId: 'de-opportunity-card',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'germany.job_seeker',
    domain: 'germany',
    path: '/germany-job-seeker-assessment',
    element: createProtectedRoute(<GermanyJobSeekerAssessment />, {
      tier: 'premium',
      meta: {
        title: 'Germany Job Seeker Assessment',
        region: 'DE',
        intent: 'assessment',
        aiTags: ['job seeker', 'visa', 'assessment'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // GermanyToolsEcosystem.tsx catalog id 'job-seeker-visa'.
        toolId: 'de-job-seeker-visa',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'germany.blue_card',
    domain: 'germany',
    path: '/germany-eu-blue-card-assessment',
    element: createProtectedRoute(<GermanyEUBlueCardAssessment />, {
      tier: 'premium',
      meta: {
        title: 'Germany EU Blue Card Assessment',
        region: 'DE',
        intent: 'assessment',
        aiTags: ['blue card', 'eu blue card', 'assessment'],
        // Tool-usage tracking pilot (see docs on RouteMeta.toolId) — matches
        // the GermanyToolsEcosystem.tsx catalog id 'blue-card-assessment'.
        toolId: 'de-blue-card-assessment',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'germany.skilled_immigration',
    domain: 'germany',
    path: '/germany-skilled-immigration-assessment',
    element: createProtectedRoute(<GermanySkilledImmigrationAssessment />, {
      tier: 'premium',
      meta: {
        title: 'Germany Skilled Immigration Assessment',
        region: 'DE',
        intent: 'assessment',
        aiTags: ['skilled immigration', 'fachkräfte', 'assessment'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // GermanyToolsEcosystem.tsx catalog id 'skilled-immigration-act'.
        toolId: 'de-skilled-immigration-act',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'germany.student',
    domain: 'germany',
    path: '/germany-student-assessment',
    element: createProtectedRoute(<GermanyStudentAssessment />, {
      tier: 'premium',
      meta: {
        title: 'Germany Student Visa Assessment',
        region: 'DE',
        intent: 'assessment',
        aiTags: ['student', 'visa', 'assessment'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // GermanyToolsEcosystem.tsx catalog id 'student-visa-eligibility'.
        toolId: 'de-student-visa-eligibility',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'germany.family_reunion',
    domain: 'germany',
    path: '/germany-family-reunion-assessment',
    element: createProtectedRoute(<GermanyFamilyReunionAssessment />, {
      tier: 'premium',
      meta: {
        title: 'Germany Family Reunion Assessment',
        region: 'DE',
        intent: 'assessment',
        aiTags: ['family reunion', 'spouse', 'assessment'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // GermanyToolsEcosystem.tsx catalog id 'family-reunion-eligibility'.
        toolId: 'de-family-reunion-eligibility',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'germany.freelancer',
    domain: 'germany',
    path: '/germany-freelancer-assessment',
    element: createProtectedRoute(<GermanyFreelancerAssessment />, {
      tier: 'premium',
      meta: {
        title: 'Germany Freelancer Assessment',
        region: 'DE',
        intent: 'assessment',
        aiTags: ['freelancer', 'self-employed', 'assessment'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // GermanyToolsEcosystem.tsx catalog id 'freelancer-visa'.
        toolId: 'de-freelancer-visa',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'germany.assessment_result',
    domain: 'germany',
    path: '/germany-assessment-result-analysis',
    element: createProtectedRoute(<GermanyAssessmentResultAnalysis />, {
      tier: 'premium',
      meta: {
        title: 'Germany Assessment Result Analysis',
        region: 'DE',
        intent: 'analysis',
        aiTags: ['assessment', 'result', 'analysis'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // GermanyToolsEcosystem.tsx catalog id 'assessment-result-analysis'.
        toolId: 'de-assessment-result-analysis',
        category: 'assessments',
      },
    }),
  },
];